import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    FaSearch, FaPlus, FaEdit, FaTrash, FaEye, FaPrint,
    FaFileExcel, FaCheck, FaTimes, FaDownload, FaBan, FaFilter
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../../../context/TitleContext';
import {
    fetchEmployeePenalties,
    deleteEmployeePenalty,
    approveEmployeePenalty,
    cancelEmployeePenalty,
} from '../../../../redux/Slices/EmployeePenaltiesSlice';
import { getEmployees } from '../../../../redux/Slices/EmployeesSlice';
import { fetchPenaltyTypes } from '../../../../redux/Slices/PenaltyTypesSlice';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getPdfRowStyles, chunkArray, toPositiveInteger, sortListById } from '../../../../shared/utils.js/utils';
import { isOnline } from '../../../../shared/utils.js/offlineQueue';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import DropdownWithSearch from '../../../../shared/components/DropdownWithSearch';

const EmployeePenalties = () => {
    const { t, i18n } = useTranslation("");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { setTitle } = useTitle();
    const isRTL = i18n.language === 'ar';
    const dir = isRTL ? 'rtl' : 'ltr';
    const textAlign = isRTL ? 'right' : 'left';

    const { employeePenalties, loading } = useSelector(
        (state) => state.employeePenalties
    );

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPenalty, setSelectedPenalty] = useState(null);

    // Preview State
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const isFirstRun = useRef(true);

    // Print State
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfType, setPdfType] = useState(null);
    const [pdfChunks, setPdfChunks] = useState([]);
    const [allPenaltiesForPdf, setAllPenaltiesForPdf] = useState([]);

    const publicUrl = process.env.PUBLIC_URL || '';
    const headerLogo = `${publicUrl}/ACE.png`;

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [perPageInput, setPerPageInput] = useState(10);

    // Sorting State
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null

    // Filters State
    const [isFilterOpen, setIsFilterOpen] = useState(true);
    const [filters, setFilters] = useState({
        employee_id: '',
        penalty_type_id: '',
        status: '',
        start_date: '',
        end_date: '',
    });

    // Employee dropdown search state
    const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [employeePage, setEmployeePage] = useState(1);
    const [employeePagination, setEmployeePagination] = useState(null);
    const employeeSearchInputRef = useRef(null);

    // Penalty Type dropdown search state
    const [penaltyTypeDropdownOpen, setPenaltyTypeDropdownOpen] = useState(false);
    const [penaltyTypeSearchTerm, setPenaltyTypeSearchTerm] = useState('');
    const [penaltyTypeOptions, setPenaltyTypeOptions] = useState([]);
    const [penaltyTypePage, setPenaltyTypePage] = useState(1);
    const [penaltyTypePagination, setPenaltyTypePagination] = useState(null);
    const penaltyTypeSearchInputRef = useRef(null);

    // Local state for penalties
    const [rawEmployeePenalties, setRawEmployeePenalties] = useState([]);
    const [isLoadingPenalties, setIsLoadingPenalties] = useState(false);

    useEffect(() => {
        setTitle('sidenav.hr.employeePenaltiesNamespace.title');
    }, [setTitle]);

    // Fetch all penalties on mount
    const loadAllPenalties = useCallback(async () => {
        setIsLoadingPenalties(true);
        try {
            const params = { page: 1, per_page: 1000 };
            await dispatch(fetchEmployeePenalties(params)).unwrap();
        } catch (err) {
            if (err !== 'OFFLINE_REQUEST_SAVED') {
                console.error('Failed to fetch employee penalties:', err);
            }
        } finally {
            setIsLoadingPenalties(false);
        }
    }, [dispatch]);

    useEffect(() => {
        loadAllPenalties();
    }, [loadAllPenalties]);

    // Sync Redux state with local state
    useEffect(() => {
        if (employeePenalties) {
            const list = Array.isArray(employeePenalties) ? employeePenalties : (employeePenalties.data || []);
            setRawEmployeePenalties(list);
        }
    }, [employeePenalties]);

    // Sync offline updates
    useEffect(() => {
        const handleSync = async (event) => {
            const detail = event?.detail || {};
            if (detail.count > 0 && detail.results) {
                const hasPenaltyRequest = detail.results.some(r =>
                    r.success === true &&
                    r.request?.url?.includes('/employee-penalties') &&
                    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(r.request?.method)
                );
                if (hasPenaltyRequest && isOnline()) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    loadAllPenalties();
                }
            }
        };

        window.addEventListener('offlineRequestsRetried', handleSync);
        return () => {
            window.removeEventListener('offlineRequestsRetried', handleSync);
        };
    }, [loadAllPenalties]);

    // Helper to normalize lists
    const normalizeListResponse = (payload) => {
        if (!payload) return { items: [], pagination: null };
        let items = [];
        let pagination = null;
        if (Array.isArray(payload?.data?.data)) {
            items = payload.data.data;
            pagination = payload.data?.meta || payload.data?.pagination || null;
        } else if (Array.isArray(payload?.data)) {
            items = payload.data;
            pagination = payload.meta || payload.pagination || null;
        } else if (Array.isArray(payload?.items)) {
            items = payload.items;
            pagination = payload.pagination || payload.meta || null;
        } else if (Array.isArray(payload)) {
            items = payload;
            pagination = null;
        } else {
            items = [];
            pagination = payload.pagination || payload.meta || null;
        }
        if (pagination) {
            pagination = {
                current_page: pagination.current_page || pagination.page || 1,
                last_page: pagination.last_page || pagination.total_pages || 1,
                per_page: pagination.per_page || 10,
                total: pagination.total || 0,
            };
        }
        return { items, pagination };
    };

    // Fetch dropdown options
    const fetchEmployeesForFilter = useCallback(
        async (page = employeePage, search = employeeSearchTerm) => {
            try {
                const payload = await dispatch(
                    getEmployees({
                        page,
                        per_page: 10,
                        search: search || undefined,
                        language: i18n.language,
                    })
                ).unwrap();
                const { items, pagination: paginationMeta } = normalizeListResponse(payload);
                setEmployeeOptions(items);
                setEmployeePagination(paginationMeta);
            } catch (error) {
                console.error("Failed to fetch employees for filter:", error);
            }
        },
        [dispatch, i18n.language]
    );

    const fetchPenaltyTypesForFilter = useCallback(
        async (page = penaltyTypePage, search = penaltyTypeSearchTerm) => {
            try {
                const payload = await dispatch(
                    fetchPenaltyTypes({
                        page,
                        per_page: 10,
                        search: search || undefined,
                    })
                ).unwrap();
                const { items, pagination: paginationMeta } = normalizeListResponse(payload);
                setPenaltyTypeOptions(items);
                setPenaltyTypePagination(paginationMeta);
            } catch (error) {
                console.error("Failed to fetch penalty types for filter:", error);
            }
        },
        [dispatch]
    );

    // Dropdown effects
    useEffect(() => {
        if (employeeDropdownOpen) {
            fetchEmployeesForFilter(employeePage, employeeSearchTerm);
        }
    }, [employeeDropdownOpen, employeePage, fetchEmployeesForFilter]);

    useEffect(() => {
        if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
            const timeoutId = setTimeout(() => {
                setEmployeePage(1);
                fetchEmployeesForFilter(1, employeeSearchTerm);
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [employeeSearchTerm, employeeDropdownOpen, fetchEmployeesForFilter]);

    useEffect(() => {
        if (penaltyTypeDropdownOpen) {
            fetchPenaltyTypesForFilter(penaltyTypePage, penaltyTypeSearchTerm);
        }
    }, [penaltyTypeDropdownOpen, penaltyTypePage, fetchPenaltyTypesForFilter]);

    useEffect(() => {
        if (penaltyTypeSearchTerm !== undefined && penaltyTypeDropdownOpen) {
            const timeoutId = setTimeout(() => {
                setPenaltyTypePage(1);
                fetchPenaltyTypesForFilter(1, penaltyTypeSearchTerm);
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [penaltyTypeSearchTerm, penaltyTypeDropdownOpen, fetchPenaltyTypesForFilter]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
            const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');
            if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
                setEmployeeDropdownOpen(false);
                setPenaltyTypeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter, search and sorting logic
    const filteredEmployeePenalties = React.useMemo(() => {
        let data = [...rawEmployeePenalties];

        // 1. Search term
        const query = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
        if (query) {
            data = data.filter((item) => {
                const amountStr = item.amount ? String(item.amount) : '';
                const empNameAr = (item.employee?.name_ar || '').toLowerCase();
                const empNameEn = (item.employee?.name_en || '').toLowerCase();
                const empName = (item.employee?.name || '').toLowerCase();
                const empCode = (item.employee?.employee_code || '').toLowerCase();
                const ptNameAr = (item.penalty_type?.name_ar || '').toLowerCase();
                const ptNameEn = (item.penalty_type?.name_en || '').toLowerCase();
                const ptName = (item.penalty_type?.name || '').toLowerCase();
                const pDate = (item.penalty_date || '').toLowerCase();
                const status = (item.status || '').toLowerCase();
                const statusTrans = t(`sidenav.hr.employeePenaltiesNamespace.${item.status}`).toLowerCase();
                const desc = (item.description || '').toLowerCase();

                return empNameAr.includes(query) ||
                       empNameEn.includes(query) ||
                       empName.includes(query) ||
                       empCode.includes(query) ||
                       ptNameAr.includes(query) ||
                       ptNameEn.includes(query) ||
                       ptName.includes(query) ||
                       amountStr.includes(query) ||
                       pDate.includes(query) ||
                       status.includes(query) ||
                       statusTrans.includes(query) ||
                       desc.includes(query);
            });
        }

        // 2. Employee filter
        if (filters.employee_id) {
            data = data.filter(item => String(item.employee?.id) === String(filters.employee_id));
        }

        // 3. Penalty Type filter
        if (filters.penalty_type_id) {
            data = data.filter(item => String(item.penalty_type?.id) === String(filters.penalty_type_id));
        }

        // 4. Status filter
        if (filters.status) {
            data = data.filter(item => item.status === filters.status);
        }

        // 5. Date range filter
        if (filters.start_date) {
            const start = new Date(filters.start_date).getTime();
            data = data.filter(item => new Date(item.penalty_date).getTime() >= start);
        }
        if (filters.end_date) {
            const end = new Date(filters.end_date).getTime();
            data = data.filter(item => new Date(item.penalty_date).getTime() <= end);
        }

        // 6. Sorting
        if (sortField && sortOrder) {
            data.sort((a, b) => {
                let aVal = '';
                let bVal = '';

                if (sortField === 'employee') {
                    aVal = isRTL ? (a.employee?.name_ar || a.employee?.name || '') : (a.employee?.name_en || a.employee?.name || '');
                    bVal = isRTL ? (b.employee?.name_ar || b.employee?.name || '') : (b.employee?.name_en || b.employee?.name || '');
                } else if (sortField === 'penalty_type') {
                    aVal = isRTL ? (a.penalty_type?.name_ar || a.penalty_type?.name || '') : (a.penalty_type?.name_en || a.penalty_type?.name || '');
                    bVal = isRTL ? (b.penalty_type?.name_ar || b.penalty_type?.name || '') : (b.penalty_type?.name_en || b.penalty_type?.name || '');
                } else if (sortField === 'amount') {
                    const aNum = Number(a.amount) || 0;
                    const bNum = Number(b.amount) || 0;
                    return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
                } else if (sortField === 'date') {
                    const aTime = new Date(a.penalty_date || 0).getTime();
                    const bTime = new Date(b.penalty_date || 0).getTime();
                    return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
                } else if (sortField === 'status') {
                    aVal = t(`sidenav.hr.employeePenaltiesNamespace.${a.status}`);
                    bVal = t(`sidenav.hr.employeePenaltiesNamespace.${b.status}`);
                }

                return sortOrder === 'asc'
                    ? aVal.localeCompare(bVal, isRTL ? 'ar' : 'en', { sensitivity: 'base' })
                    : bVal.localeCompare(aVal, isRTL ? 'ar' : 'en', { sensitivity: 'base' });
            });
        }

        return data;
    }, [rawEmployeePenalties, searchTerm, filters, sortField, sortOrder, isRTL, t]);

    // Reset pagination to page 1 on search or filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filters]);

    const totalPages = Math.max(1, Math.ceil(filteredEmployeePenalties.length / perPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedEmployeePenalties = filteredEmployeePenalties.slice(
        (currentPageSafe - 1) * perPage,
        currentPageSafe * perPage
    );

    const handleApplyPerPage = () => {
        const safePerPage = toPositiveInteger(perPageInput, perPage);
        setPerPageInput(safePerPage);
        if (safePerPage !== perPage) {
            setPerPage(safePerPage);
            setCurrentPage(1);
        }
    };

    const handleEdit = (penalty) => {
        navigate(`/employee-penalties/edit/${penalty.id}`);
    };

    const handleDeleteClick = (penalty) => {
        setSelectedPenalty(penalty);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedPenalty) return;
        const backup = selectedPenalty;
        // Optimistic delete
        setRawEmployeePenalties(prev => prev.filter(ep => ep.id !== backup.id));
        setShowDeleteModal(false);
        setSelectedPenalty(null);

        try {
            const result = await dispatch(deleteEmployeePenalty(backup.id)).unwrap();
            if (result && result.isOffline) {
                localStorage.setItem('offline_add_performed', 'true');
                toast.success(isRTL ? 'تم حذف الجزاء محلياً وسيتم المزامنة عند عودة الاتصال' : 'Penalty deleted locally and will sync when online');
            } else {
                toast.success(t('sidenav.hr.employeePenaltiesNamespace.deleteSuccess'));
            }
            loadAllPenalties();
        } catch (error) {
            setRawEmployeePenalties(prev => [...prev, backup]);
            toast.error(error?.message || t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
        }
    };

    const handleApprove = async (penalty) => {
        try {
            const result = await dispatch(approveEmployeePenalty(penalty.id)).unwrap();
            if (result && result.isOffline) {
                localStorage.setItem('offline_add_performed', 'true');
                toast.success(isRTL ? 'تمت الموافقة محلياً وسيتم المزامنة' : 'Approved locally and will sync');
            } else {
                toast.success(t('sidenav.hr.employeePenaltiesNamespace.approveSuccess'));
            }
            loadAllPenalties();
        } catch (error) {
            toast.error(error?.message || t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
        }
    };

    const handleCancel = async (penalty) => {
        try {
            const result = await dispatch(cancelEmployeePenalty(penalty.id)).unwrap();
            if (result && result.isOffline) {
                localStorage.setItem('offline_add_performed', 'true');
                toast.success(isRTL ? 'تم الإلغاء محلياً وسيتم المزامنة' : 'Cancelled locally and will sync');
            } else {
                toast.success(t('sidenav.hr.employeePenaltiesNamespace.cancelSuccess'));
            }
            loadAllPenalties();
        } catch (error) {
            toast.error(error?.message || t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            employee_id: '',
            penalty_type_id: '',
            status: '',
            start_date: '',
            end_date: '',
        });
        setSearchTerm('');
        setCurrentPage(1);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            if (sortOrder === 'asc') {
                setSortOrder('desc');
            } else if (sortOrder === 'desc') {
                setSortField(null);
                setSortOrder(null);
            } else {
                setSortOrder('asc');
            }
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const renderSortIndicator = (field) => {
        if (sortField !== field) {
            return <span style={{ color: '#888', marginInlineStart: '6px', fontSize: '12px' }}>⇅</span>;
        }
        return sortOrder === 'asc'
            ? <span style={{ color: 'white', marginInlineStart: '6px', fontSize: '12px' }}>▲</span>
            : <span style={{ color: 'white', marginInlineStart: '6px', fontSize: '12px' }}>▼</span>;
    };

    // --- Dynamic Chunking (PDF Pagination Engine) ---
    const colStyles = {
        serial: { width: '8%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
        employee: { width: '25%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
        penalty_type: { width: '20%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
        amount: { width: '12%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
        date: { width: '15%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
        status: { width: '20%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' }
    };

    const calculateDynamicChunks = (items) => {
        const tableContainer = document.getElementById('employee-penalties-measurement-container');
        const thead = tableContainer ? tableContainer.querySelector('thead') : null;
        const theadHeight = thead ? thead.offsetHeight : 35;
        const rows = tableContainer ? tableContainer.querySelectorAll('tbody tr') : [];

        const MAX_TABLE_HEIGHT = 740;
        const chunks = [];
        let currentChunk = [];
        let currentHeight = theadHeight;

        items.forEach((item, index) => {
            const rowElement = rows[index];
            let rowHeight = 45; // Default fallback height

            if (rowElement && rowElement.offsetHeight > 0) {
                rowHeight = rowElement.offsetHeight;
            } else {
                // Fallback calculations in case elements aren't rendered
                const empName = isRTL ? (item.employee?.name_ar || '') : (item.employee?.name_en || '');
                const ptName = isRTL ? (item.penalty_type?.name_ar || '') : (item.penalty_type?.name_en || '');
                const maxTextLen = Math.max(empName.length / 20, ptName.length / 15);
                const lines = Math.max(1, Math.ceil(maxTextLen));
                rowHeight = 35 + (lines * 15);
            }

            if (currentHeight + rowHeight > MAX_TABLE_HEIGHT && currentChunk.length > 0) {
                chunks.push(currentChunk);
                currentChunk = [];
                currentHeight = theadHeight;
            }
            currentChunk.push(item);
            currentHeight += rowHeight;
        });

        if (currentChunk.length > 0) {
            chunks.push(currentChunk);
        }
        return chunks;
    };

    const handlePreviewSingle = (penalty) => {
        setPreviewData({ type: 'single', data: penalty });
        setShowPreviewModal(true);
    };

    const handlePreviewAll = async () => {
        if (!filteredEmployeePenalties || filteredEmployeePenalties.length === 0) {
            toast.error(t('sidenav.hr.employeePenaltiesNamespace.noData'));
            return;
        }
        setAllPenaltiesForPdf(filteredEmployeePenalties);
        await new Promise((resolve) => setTimeout(resolve, 300));
        const chunks = calculateDynamicChunks(filteredEmployeePenalties);
        setPdfChunks(chunks);
        setShowPreviewModal(true);
    };

    const handlePrintFromPreview = async () => {
        if (!previewData) return;
        setShowPreviewModal(false);
        if (previewData.type === 'single') {
            await handlePrintSingle(previewData.data);
        } else {
            await handlePrintAll();
        }
    };

    const handlePrintSingle = async (penalty) => {
        try {
            setSelectedPenalty(penalty);
            setPdfType('single');
            await new Promise((resolve) => requestAnimationFrame(() => resolve()));

            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            }
            await new Promise((resolve) => setTimeout(resolve, 300));

            setIsGeneratingPdf(true);

            const element = document.getElementById(`employee-penalty-pdf-single-${penalty.id}`);
            if (!element) {
                toast.error(t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
                return;
            }

            const devicePixelRatio = window.devicePixelRatio || 1;
            const scale = Math.max(3, devicePixelRatio * 2);

            const canvas = await html2canvas(element, {
                scale: scale,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                letterRendering: true,
                allowTaint: false,
                removeContainer: false,
                imageTimeout: 15000,
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.getElementById(`employee-penalty-pdf-single-${penalty.id}`);
                    if (clonedElement) {
                        clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                        clonedElement.style.fontWeight = 'bold';
                        clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
                    }
                }
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imgData = canvas.toDataURL('image/png', 1.0);
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');

            pdf.save(`ACE_Penalty_${penalty.id}_${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success(t('btns.download'));
        } catch (err) {
            console.error('Penalty PDF error:', err);
            toast.error(t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
        } finally {
            setIsGeneratingPdf(false);
            setSelectedPenalty(null);
            setPdfType(null);
        }
    };

    const handlePrintAll = async () => {
        if (!filteredEmployeePenalties || filteredEmployeePenalties.length === 0) {
            toast.error(t('sidenav.hr.employeePenaltiesNamespace.noData'));
            return;
        }

        try {
            setAllPenaltiesForPdf(filteredEmployeePenalties);
            await new Promise((resolve) => setTimeout(resolve, 300));

            const chunks = calculateDynamicChunks(filteredEmployeePenalties);
            setPdfChunks(chunks);
            setPdfType('all');

            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => setTimeout(resolve, 100));

            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            }
            setIsGeneratingPdf(true);

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const devicePixelRatio = window.devicePixelRatio || 1;
            const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

            for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
                await new Promise((resolve) => requestAnimationFrame(resolve));
                await new Promise((resolve) => setTimeout(resolve, 100));

                const element = document.getElementById(`employee-penalties-pdf-all-${chunkIndex}`);
                if (!element) {
                    continue;
                }

                const canvas = await html2canvas(element, {
                    scale,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    letterRendering: false,
                    allowTaint: false,
                    removeContainer: false,
                    imageTimeout: 10000,
                    onclone: (clonedDoc) => {
                        const clonedElement = clonedDoc.getElementById(`employee-penalties-pdf-all-${chunkIndex}`);
                        if (clonedElement) {
                            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                            clonedElement.style.fontWeight = 'bold';
                            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
                        }
                    }
                });

                const imgData = canvas.toDataURL('image/png', 1.0);
                const imgHeight = (canvas.height * pdfWidth) / canvas.width;

                if (chunkIndex > 0) {
                    pdf.addPage();
                }

                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
            }

            pdf.save(`${t('sidenav.hr.employeePenaltiesNamespace.print.fileName') || 'Employee_Penalties'}_${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success(t('btns.download'));
        } catch (error) {
            console.error('Employee Penalties PDF error:', error);
            toast.error(t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
        } finally {
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
        }
    };

    const handleExportExcel = () => {
        if (!filteredEmployeePenalties || filteredEmployeePenalties.length === 0) {
            toast.error(t('sidenav.hr.employeePenaltiesNamespace.noData'));
            return;
        }

        try {
            const excelData = filteredEmployeePenalties.map((item, index) => ({
                '#': index + 1,
                [t('sidenav.hr.employeePenaltiesNamespace.employee')]: isRTL ? (item.employee?.name_ar || item.employee?.name || '') : (item.employee?.name_en || item.employee?.name || ''),
                [t('sidenav.hr.employeePenaltiesNamespace.employee') + ' Code']: item.employee?.employee_code || '',
                [t('sidenav.hr.employeePenaltiesNamespace.penaltyType')]: isRTL ? (item.penalty_type?.name_ar || item.penalty_type?.name || '') : (item.penalty_type?.name_en || item.penalty_type?.name || ''),
                [t('sidenav.hr.employeePenaltiesNamespace.amount')]: item.amount,
                [t('sidenav.hr.employeePenaltiesNamespace.date')]: item.penalty_date,
                [t('sidenav.hr.employeePenaltiesNamespace.status')]: t(`sidenav.hr.employeePenaltiesNamespace.${item.status}`),
                [t('sidenav.hr.employeePenaltiesNamespace.description')]: item.description || ''
            }));

            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'الجزاءات' : 'Employee Penalties');
            const fileName = `${isRTL ? 'جزاءات_الموظفين' : 'Employee_Penalties'}_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);
            toast.success(isRTL ? 'تم تصدير ملف Excel بنجاح' : 'Excel exported successfully');
        } catch (e) {
            console.error(e);
            toast.error(t('sidenav.hr.employeePenaltiesNamespace.errorAction'));
        }
    };

    // --- PDF Render Functions ---
    const renderSinglePdfPage = (penalty, isPreviewMode = false) => {
        const containerId = isPreviewMode
            ? `preview-employee-penalty-pdf-${penalty.id}`
            : `employee-penalty-pdf-single-${penalty.id}`;

        return (
            <div
                id={containerId}
                style={{
                    width: '794px',
                    height: '1123px',
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    padding: '40px',
                    boxSizing: 'border-box',
                    boxShadow: isPreviewMode ? '0 4px 20px rgba(0, 0, 0, 0.5)' : 'none',
                    overflow: 'hidden'
                }}
            >
                <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
                    <div style={{
                        border: '2px solid #000',
                        height: '1033px',
                        padding: '18px 18px 10px',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: 'bold',
                        direction: isRTL ? 'rtl' : 'ltr',
                        backgroundColor: '#ffffff',
                        boxSizing: 'border-box',
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }} crossOrigin="anonymous" />

                            <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0 }}>
                                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تفاصيل جزاء الموظف' : 'Employee Penalty Details'}</div>
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>ID: #{penalty.id}</div>
                            </div>

                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: '120px' }}>
                                <div style={{ marginTop: '12px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                                    {[
                                        { label: t('sidenav.hr.employeePenaltiesNamespace.date'), value: penalty.penalty_date || '—', accent: true },
                                        { label: t('sidenav.hr.employeePenaltiesNamespace.employee'), value: isRTL ? (penalty.employee?.name_ar || penalty.employee?.name || '') : (penalty.employee?.name_en || penalty.employee?.name || '') },
                                        { label: t('sidenav.hr.employeePenaltiesNamespace.penaltyType'), value: isRTL ? (penalty.penalty_type?.name_ar || penalty.penalty_type?.name || '') : (penalty.penalty_type?.name_en || penalty.penalty_type?.name || '') },
                                        { label: t('sidenav.hr.employeePenaltiesNamespace.amount'), value: penalty.amount ? parseFloat(penalty.amount).toLocaleString() : '0', accent: true },
                                        { label: t('sidenav.hr.employeePenaltiesNamespace.status'), value: t(`sidenav.hr.employeePenaltiesNamespace.${penalty.status}`), accent: true, statusVal: penalty.status },
                                        { label: t('sidenav.hr.employeePenaltiesNamespace.description'), value: penalty.description || '—' }
                                    ].map((row, idx) => {
                                        let valColor = '#000';
                                        if (row.statusVal) {
                                            valColor = row.statusVal === 'approved' ? '#28a745' : row.statusVal === 'cancelled' ? '#dc3545' : '#ffc107';
                                        } else if (row.accent) {
                                            valColor = '#0057c2';
                                        }
                                        return (
                                            <div key={idx} style={{ display: 'flex', borderBottom: idx === 5 ? 'none' : '1px solid #000', minHeight: '40px', backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.03)' : 'transparent' }}>
                                                {isRTL ? (
                                                    <>
                                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 15px', fontSize: '15px', fontWeight: row.accent ? '700' : '500', color: valColor, direction: 'rtl' }}>{row.value}</div>
                                                        <div style={{ width: '30%', borderRight: '1px solid #000', backgroundColor: 'rgba(227,242,253,0.4)', display: 'flex', alignItems: 'center', padding: '0 15px', fontWeight: '700', fontSize: '14px', color: '#1976d2', direction: 'rtl' }}>{row.label}</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div style={{ width: '30%', borderRight: '1px solid #000', backgroundColor: 'rgba(227,242,253,0.4)', display: 'flex', alignItems: 'center', padding: '0 15px', fontWeight: '700', fontSize: '14px', color: '#1976d2' }}>{row.label}</div>
                                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 15px', fontSize: '15px', fontWeight: row.accent ? '700' : '500', color: valColor }}>{row.value}</div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px' }}>
                                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPdfPage = (chunk, chunkIdx, totalChunks, elementId = null) => {
        const hasActiveFilters = searchTerm.trim() || filters.employee_id || filters.penalty_type_id || filters.status || filters.start_date || filters.end_date;

        return (
            <div
                id={elementId}
                style={{
                    width: '794px',
                    height: '1123px',
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    padding: '40px',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}
            >
                <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
                    <div style={{
                        border: '2px solid #000',
                        height: '1033px',
                        padding: '18px 18px 10px',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: 'bold',
                        direction: isRTL ? 'rtl' : 'ltr',
                        backgroundColor: '#ffffff',
                        boxSizing: 'border-box',
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '60%', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat', opacity: 0.05, zIndex: 0, pointerEvents: 'none' }} />
                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }} crossOrigin="anonymous" />

                            <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0 }}>
                                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير جزاءات الموظفين' : 'Employee Penalties Report'}</div>
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                                    {isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalChunks}
                                </div>
                                {hasActiveFilters && (
                                    <div style={{ fontSize: '9px', color: '#444', marginTop: '6px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', backgroundColor: 'rgba(238,242,247,0.5)', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                        {searchTerm.trim() && <span>{isRTL ? 'بحث' : 'Search'}: {searchTerm.trim()}</span>}
                                        {filters.employee_id && <span>{t('sidenav.hr.employeePenaltiesNamespace.employee')}: {employeeOptions.find(e => String(e.id) === String(filters.employee_id))?.name || filters.employee_id}</span>}
                                        {filters.penalty_type_id && <span>{t('sidenav.hr.employeePenaltiesNamespace.penaltyType')}: {penaltyTypeOptions.find(p => String(p.id) === String(filters.penalty_type_id))?.name || filters.penalty_type_id}</span>}
                                        {filters.status && <span>{t('sidenav.hr.employeePenaltiesNamespace.status')}: {t(`sidenav.hr.employeePenaltiesNamespace.${filters.status}`)}</span>}
                                        {(filters.start_date || filters.end_date) && <span>{t('sidenav.hr.employeePenaltiesNamespace.date')}: {filters.start_date || '...'} {isRTL ? 'إلى' : 'to'} {filters.end_date || '...'}</span>}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', flex: 1, overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", serif', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'fixed' }}>
                                    <colgroup>
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '25%' }} />
                                        <col style={{ width: '20%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '15%' }} />
                                        <col style={{ width: '20%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
                                            <th style={colStyles.serial}>#</th>
                                            <th style={colStyles.employee}>{t('sidenav.hr.employeePenaltiesNamespace.employee')}</th>
                                            <th style={colStyles.penalty_type}>{t('sidenav.hr.employeePenaltiesNamespace.penaltyType')}</th>
                                            <th style={colStyles.amount}>{t('sidenav.hr.employeePenaltiesNamespace.amount')}</th>
                                            <th style={colStyles.date}>{t('sidenav.hr.employeePenaltiesNamespace.date')}</th>
                                            <th style={colStyles.status}>{t('sidenav.hr.employeePenaltiesNamespace.status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chunk && chunk.length > 0 ? chunk.map((item, idx) => {
                                            const foundIdx = allPenaltiesForPdf.findIndex(pt => String(pt?.id) === String(item?.id));
                                            const serial = foundIdx >= 0 ? foundIdx + 1 : chunkIdx * 25 + idx + 1;
                                            const cellBg = idx % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(245,245,245,0.5)';
                                            return (
                                                <tr key={item.id} style={{ backgroundColor: cellBg }}>
                                                    <td style={{ ...colStyles.serial, backgroundColor: cellBg }}>{serial}</td>
                                                    <td style={{ ...colStyles.employee, backgroundColor: cellBg }}>{isRTL ? (item.employee?.name_ar || item.employee?.name || '') : (item.employee?.name_en || item.employee?.name || '')}</td>
                                                    <td style={{ ...colStyles.penalty_type, backgroundColor: cellBg }}>{isRTL ? (item.penalty_type?.name_ar || item.penalty_type?.name || '') : (item.penalty_type?.name_en || item.penalty_type?.name || '')}</td>
                                                    <td style={{ ...colStyles.amount, backgroundColor: cellBg }}>{parseFloat(item.amount).toLocaleString()}</td>
                                                    <td style={{ ...colStyles.date, backgroundColor: cellBg }}>{item.penalty_date}</td>
                                                    <td style={{ ...colStyles.status, backgroundColor: cellBg, color: item.status === 'approved' ? '#28a745' : item.status === 'cancelled' ? '#dc3545' : '#ffc107' }}>{t(`sidenav.hr.employeePenaltiesNamespace.${item.status}`)}</td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr><td colSpan="6" style={{ border: '1px solid #000', padding: '15px', textAlign: 'center', color: '#666', fontSize: '12px' }}>{t('sidenav.hr.employeePenaltiesNamespace.noData')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px' }}>
                                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                    {t('sidenav.hr.employeePenaltiesNamespace.title')}
                </h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => navigate('/employee-penalties/add')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                        <FaPlus /> {t('sidenav.hr.employeePenaltiesNamespace.add')}
                    </button>
                    <button onClick={handlePreviewAll} disabled={isGeneratingPdf} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                        <FaEye /> {t('sidenav.hr.penaltyTypesNamespace.previewAll')}
                    </button>
                    <button onClick={() => handlePrintAll()} disabled={isGeneratingPdf} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                        <FaPrint /> {t('sidenav.hr.penaltyTypesNamespace.printAll')}
                    </button>
                    <button onClick={handleExportExcel} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                        <FaFileExcel /> {t('sidenav.hr.penaltyTypesNamespace.exportAll')}
                    </button>
                </div>
            </div>

            {/* Filters panel */}
            <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                border: '1px solid #333'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: isFilterOpen ? '20px' : '0',
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    cursor: 'pointer'
                }}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                    {isRTL ? null : <FaFilter style={{ fontSize: '18px', color: '#AC2000' }} />}
                    <h2 style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: 'white',
                        margin: 0,
                        textAlign: isRTL ? 'right' : 'left',
                        marginLeft: isRTL ? 'auto' : 0,
                        marginRight: isRTL ? 0 : 'auto',
                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                    }}>
                        {t('leaveRequests.filter.title', { defaultValue: isRTL ? 'الفلاتر' : 'Filters' })}
                    </h2>
                    {isRTL ? <FaFilter style={{ fontSize: '18px', color: '#AC2000' }} /> : null}
                    <span style={{ color: 'white', fontSize: '16px', marginLeft: isRTL ? '0' : '8px', marginRight: isRTL ? '8px' : '0' }}>
                        {isFilterOpen ? '▼' : (isRTL ? '◀' : '▶')}
                    </span>
                </div>

                {isFilterOpen && (
                    <>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px',
                            marginBottom: '20px'
                        }}>
                            {/* Search Filter */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#ccc',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}>
                                    {t('common.search', { defaultValue: isRTL ? 'بحث' : 'Search' })}
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        placeholder={t('sidenav.hr.employeePenaltiesNamespace.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: isRTL ? '12px 35px 12px 15px' : '12px 15px 12px 35px',
                                            backgroundColor: '#14263b',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            minHeight: '45px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left',
                                            boxSizing: 'border-box',
                                            outline: 'none'
                                        }}
                                    />
                                    <FaSearch style={{
                                        position: 'absolute',
                                        [isRTL ? 'right' : 'left']: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#888',
                                        fontSize: '14px'
                                    }} />
                                </div>
                            </div>

                            {/* Employee Filter */}
                            <div style={{ position: 'relative' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#ccc',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })}
                                </label>
                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div
                                        data-dropdown-toggle
                                        onClick={() => {
                                            setEmployeeDropdownOpen(!employeeDropdownOpen);
                                            if (!employeeDropdownOpen) {
                                                fetchEmployeesForFilter(1, '');
                                            }
                                        }}
                                        style={{
                                            backgroundColor: '#14263b',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            padding: '12px 15px',
                                            color: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            minHeight: '45px',
                                            direction: isRTL ? 'rtl' : 'ltr'
                                        }}
                                    >
                                        <span style={{ fontSize: '14px', color: filters.employee_id ? 'white' : '#888', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                                            {filters.employee_id
                                                ? employeeOptions.find(emp => String(emp.id) === String(filters.employee_id))?.name || t('leaveRequests.filter.selectEmployee', { defaultValue: isRTL ? 'اختر الموظف' : 'Select Employee' })
                                                : t('leaveRequests.filter.selectEmployee', { defaultValue: isRTL ? 'اختر الموظف' : 'Select Employee' })}
                                        </span>
                                        <span style={{ fontSize: 12 }}>▼</span>
                                    </div>
                                    {employeeDropdownOpen && (
                                        <DropdownWithSearch
                                            isOpen={employeeDropdownOpen}
                                            searchTerm={employeeSearchTerm}
                                            onSearchChange={setEmployeeSearchTerm}
                                            searchInputRef={employeeSearchInputRef}
                                            items={employeeOptions}
                                            pagination={employeePagination}
                                            currentPage={employeePage}
                                            onPageChange={setEmployeePage}
                                            onClose={() => setEmployeeDropdownOpen(false)}
                                            onSelect={(id) => {
                                                handleFilterChange('employee_id', String(id));
                                                setEmployeeDropdownOpen(false);
                                                setEmployeeSearchTerm('');
                                                setEmployeePage(1);
                                            }}
                                            label={t('sidenav.hr.employeePenaltiesNamespace.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })}
                                            isRTL={isRTL}
                                            t={t}
                                            searchPlaceholder={isRTL ? 'ابحث عن موظف...' : 'Search for employee...'}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Penalty Type Filter */}
                            <div style={{ position: 'relative' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#ccc',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.penaltyType', { defaultValue: isRTL ? 'نوع الجزاء' : 'Penalty Type' })}
                                </label>
                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div
                                        data-dropdown-toggle
                                        onClick={() => {
                                            setPenaltyTypeDropdownOpen(!penaltyTypeDropdownOpen);
                                            if (!penaltyTypeDropdownOpen) {
                                                fetchPenaltyTypesForFilter(1, '');
                                            }
                                        }}
                                        style={{
                                            backgroundColor: '#14263b',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            padding: '12px 15px',
                                            color: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            minHeight: '45px',
                                            direction: isRTL ? 'rtl' : 'ltr'
                                        }}
                                    >
                                        <span style={{ fontSize: '14px', color: filters.penalty_type_id ? 'white' : '#888', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                                            {filters.penalty_type_id
                                                ? penaltyTypeOptions.find(pt => String(pt.id) === String(filters.penalty_type_id))?.name || t('sidenav.hr.employeePenaltiesNamespace.selectPenaltyType', { defaultValue: isRTL ? 'اختر نوع الجزاء' : 'Select Penalty Type' })
                                                : t('sidenav.hr.employeePenaltiesNamespace.selectPenaltyType', { defaultValue: isRTL ? 'اختر نوع الجزاء' : 'Select Penalty Type' })}
                                        </span>
                                        <span style={{ fontSize: 12 }}>▼</span>
                                    </div>
                                    {penaltyTypeDropdownOpen && (
                                        <DropdownWithSearch
                                            isOpen={penaltyTypeDropdownOpen}
                                            searchTerm={penaltyTypeSearchTerm}
                                            onSearchChange={setPenaltyTypeSearchTerm}
                                            searchInputRef={penaltyTypeSearchInputRef}
                                            items={penaltyTypeOptions}
                                            pagination={penaltyTypePagination}
                                            currentPage={penaltyTypePage}
                                            onPageChange={setPenaltyTypePage}
                                            onClose={() => setPenaltyTypeDropdownOpen(false)}
                                            onSelect={(id) => {
                                                handleFilterChange('penalty_type_id', String(id));
                                                setPenaltyTypeDropdownOpen(false);
                                                setPenaltyTypeSearchTerm('');
                                                setPenaltyTypePage(1);
                                            }}
                                            label={t('sidenav.hr.employeePenaltiesNamespace.penaltyType', { defaultValue: isRTL ? 'نوع الجزاء' : 'Penalty Type' })}
                                            isRTL={isRTL}
                                            t={t}
                                            searchPlaceholder={isRTL ? 'ابحث عن نوع الجزاء...' : 'Search for penalty type...'}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Status Filter */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#ccc',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.status', { defaultValue: isRTL ? 'الحالة' : 'Status' })}
                                </label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 15px',
                                        backgroundColor: '#14263b',
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '14px',
                                        minHeight: '45px',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        outline: 'none'
                                    }}
                                >
                                    <option value="">{isRTL ? 'الكل' : 'All'}</option>
                                    <option value="pending">{t('sidenav.hr.employeePenaltiesNamespace.pending', { defaultValue: isRTL ? 'معلق' : 'Pending' })}</option>
                                    <option value="approved">{t('sidenav.hr.employeePenaltiesNamespace.approved', { defaultValue: isRTL ? 'مقبول' : 'Approved' })}</option>
                                    <option value="cancelled">{t('sidenav.hr.employeePenaltiesNamespace.cancelled', { defaultValue: isRTL ? 'ملغي' : 'Cancelled' })}</option>
                                </select>
                            </div>

                            {/* Start Date */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#ccc',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}>
                                    {isRTL ? 'من تاريخ' : 'Start Date'}
                                </label>
                                <input
                                    type="date"
                                    value={filters.start_date}
                                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 15px',
                                        backgroundColor: '#14263b',
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '14px',
                                        minHeight: '45px',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* End Date */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    color: '#ccc',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left'
                                }}>
                                    {isRTL ? 'إلى تاريخ' : 'End Date'}
                                </label>
                                <input
                                    type="date"
                                    value={filters.end_date}
                                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 15px',
                                        backgroundColor: '#14263b',
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '14px',
                                        minHeight: '45px',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Reset Filters & Pagination controls */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '15px',
                            borderTop: '1px solid #333',
                            paddingTop: '15px'
                        }}>
                            <div>
                                <button
                                    onClick={clearFilters}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                                    }}
                                >
                                    {isRTL ? 'إعادة ضبط' : 'Reset'}
                                </button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '14px', color: '#ccc', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                                    {t('sidenav.hr.penaltyTypesNamespace.perPage')}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '4px' }}>
                                    <button
                                        onClick={() => setPerPageInput(Math.max(1, toPositiveInteger(perPageInput, perPage) - 1))}
                                        style={{ padding: '8px 12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px' }}
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        value={perPageInput}
                                        onChange={(e) => setPerPageInput(toPositiveInteger(e.target.value, perPage))}
                                        style={{ width: '50px', background: 'none', border: 'none', color: 'white', textAlign: 'center', appearance: 'textfield', margin: 0, fontWeight: 'bold' }}
                                    />
                                    <button
                                        onClick={() => setPerPageInput(toPositiveInteger(perPageInput, perPage) + 1)}
                                        style={{ padding: '8px 12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px' }}
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    onClick={handleApplyPerPage}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#AC2000',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                                    }}
                                >
                                    {t('sidenav.hr.penaltyTypesNamespace.apply')}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Table */}
            <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', width: '100%' }}>
                    <table style={{ minWidth: '1000px', width: '100%', borderCollapse: 'collapse', textAlign: textAlign }}>
                        <thead>
                            <tr style={{ backgroundColor: '#AC2000' }}>
                                <th style={{ padding: '15px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>#</th>
                                <th onClick={() => handleSort('employee')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.employee')} {renderSortIndicator('employee')}
                                </th>
                                <th onClick={() => handleSort('penalty_type')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.penaltyType')} {renderSortIndicator('penalty_type')}
                                </th>
                                <th onClick={() => handleSort('amount')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.amount')} {renderSortIndicator('amount')}
                                </th>
                                <th onClick={() => handleSort('date')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.date')} {renderSortIndicator('date')}
                                </th>
                                <th onClick={() => handleSort('status')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                    {t('sidenav.hr.employeePenaltiesNamespace.status')} {renderSortIndicator('status')}
                                </th>
                                <th style={{ padding: '15px', color: 'white', fontWeight: 'bold', textAlign: 'center', whiteSpace: 'nowrap' }}>{t('btns.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && paginatedEmployeePenalties.map((item, index) => (
                                <tr key={item.id} style={{ borderBottom: '1px solid #2d3748', transition: 'background 0.2s' }}>
                                    <td style={{ padding: '15px', whiteSpace: 'nowrap' }}>{(currentPageSafe - 1) * perPage + index + 1}</td>
                                    <td style={{ padding: '15px', whiteSpace: 'nowrap' }}>{isRTL ? item.employee?.name_ar : item.employee?.name_en}</td>
                                    <td style={{ padding: '15px', whiteSpace: 'nowrap' }}>{isRTL ? item.penalty_type?.name_ar : item.penalty_type?.name_en}</td>
                                    <td style={{ padding: '15px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{parseFloat(item.amount).toLocaleString()}</td>
                                    <td style={{ padding: '15px', whiteSpace: 'nowrap' }}>{item.penalty_date}</td>
                                    <td style={{ padding: '15px', whiteSpace: 'nowrap' }}>
                                        <span style={{
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold',
                                            backgroundColor: item.status === 'approved' ? '#28a74522' : item.status === 'cancelled' ? '#dc354522' : '#ffc10722',
                                            color: item.status === 'approved' ? '#28a745' : item.status === 'cancelled' ? '#dc3545' : '#ffc107',
                                        }}>
                                            {t(`sidenav.hr.employeePenaltiesNamespace.${item.status}`)}
                                        </span>
                                    </td>
                                    <td style={{ padding: '15px' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            {item.status === 'pending' && (
                                                <button
                                                    title={t('sidenav.hr.employeePenaltiesNamespace.approve')}
                                                    onClick={() => handleApprove(item)}
                                                    style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                                                >
                                                    <FaCheck />
                                                </button>
                                            )}
                                            {item.status === 'pending' && (
                                                <button
                                                    title={t('sidenav.hr.employeePenaltiesNamespace.cancel')}
                                                    onClick={() => handleCancel(item)}
                                                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                                                >
                                                    <FaBan />
                                                </button>
                                            )}
                                            <button
                                                title={t('btns.view')}
                                                onClick={() => handlePreviewSingle(item)}
                                                style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                title={t('btns.edit')}
                                                onClick={() => handleEdit(item)}
                                                style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                title={t('btns.delete')}
                                                onClick={() => handleDeleteClick(item)}
                                                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && paginatedEmployeePenalties.length === 0 && (
                                <tr><td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap' }}>{t('sidenav.hr.employeePenaltiesNamespace.noData')}</td></tr>
                            )}
                            {loading && (
                                <tr><td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap' }}>{t('messages.loading')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                    <button
                        onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                        disabled={currentPageSafe === 1}
                        style={{ padding: '8px 12px', backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000', color: 'white', border: 'none', borderRadius: '4px', cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer' }}
                    >
                        {t('btns.previous')}
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || (p >= currentPageSafe - 2 && p <= currentPageSafe + 2)).map((p, idx, arr) => (
                        <React.Fragment key={p}>
                            {idx > 0 && p - arr[idx - 1] > 1 && <span style={{ color: '#666' }}>...</span>}
                            <button onClick={() => setCurrentPage(p)} style={{ padding: '8px 12px', backgroundColor: p === currentPageSafe ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: '4px', cursor: 'pointer' }}>
                                {p}
                            </button>
                        </React.Fragment>
                    ))}
                    <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPageSafe + 1))}
                        disabled={currentPageSafe === totalPages}
                        style={{ padding: '8px 12px', backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000', color: 'white', border: 'none', borderRadius: '4px', cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer' }}
                    >
                        {t('btns.next')}
                    </button>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && ReactDOM.createPortal(
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#1a1f2e', padding: '30px', borderRadius: '12px', maxWidth: '400px', width: '90%', textAlign: textAlign, direction: dir }}>
                        <h2 style={{ fontSize: '20px', marginBottom: '15px', color: '#fff' }}>{t('sidenav.hr.employeePenaltiesNamespace.confirmDelete')}</h2>
                        <p style={{ color: '#fff', marginBottom: '25px' }}>{t('sidenav.hr.employeePenaltiesNamespace.confirmDeleteMsg')}</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowDeleteModal(false)} style={{ padding: '8px 20px', background: 'none', border: '1px solid #333', color: 'white', borderRadius: '6px', cursor: 'pointer' }}>{t('btns.cancel')}</button>
                            <button onClick={handleDeleteConfirm} style={{ padding: '8px 20px', backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{t('btns.delete')}</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Preview Modal */}
            <PdfPreviewModal
                isOpen={showPreviewModal && previewData}
                title={isRTL ? 'معاينة PDF' : 'PDF Preview'}
                onClose={() => setShowPreviewModal(false)}
                onPrint={handlePrintFromPreview}
                isGeneratingPdf={isGeneratingPdf}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
                    {previewData?.type === 'single' ? (
                        renderSinglePdfPage(previewData.data, true)
                    ) : (
                        previewData?.chunks?.map((chunk, i) => (
                            <React.Fragment key={i}>
                                {renderPdfPage(chunk, i, previewData.chunks.length, null)}
                            </React.Fragment>
                        ))
                    )}
                </div>
            </PdfPreviewModal>

            {/* Hidden Templates for printing ALL */}
            {pdfType === 'all' && pdfChunks.map((chunk, i) => (
                <div key={i} id={`employee-penalties-pdf-all-${i}`} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                    {renderPdfPage(chunk, i, pdfChunks.length, `employee-penalties-pdf-all-${i}`)}
                </div>
            ))}

            {/* Hidden Templates for printing SINGLE */}
            {pdfType === 'single' && selectedPenalty && (
                <div id={`employee-penalty-pdf-single-${selectedPenalty.id}`} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                    {renderSinglePdfPage(selectedPenalty, false)}
                </div>
            )}

            {/* Hidden Measurement Table for dynamic heights pagination */}
            <div
                id="employee-penalties-measurement-container"
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: 0,
                    width: '714px',
                    visibility: 'hidden',
                    pointerEvents: 'none'
                }}
            >
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '25%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '20%' }} />
                    </colgroup>
                    <thead>
                        <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                            <th style={colStyles.serial}>#</th>
                            <th style={colStyles.employee}>{t('sidenav.hr.employeePenaltiesNamespace.employee')}</th>
                            <th style={colStyles.penalty_type}>{t('sidenav.hr.employeePenaltiesNamespace.penaltyType')}</th>
                            <th style={colStyles.amount}>{t('sidenav.hr.employeePenaltiesNamespace.amount')}</th>
                            <th style={colStyles.date}>{t('sidenav.hr.employeePenaltiesNamespace.date')}</th>
                            <th style={colStyles.status}>{t('sidenav.hr.employeePenaltiesNamespace.status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allPenaltiesForPdf.map((item, idx) => (
                            <tr key={item.id}>
                                <td style={colStyles.serial}>{idx + 1}</td>
                                <td style={{ ...colStyles.employee, direction: isRTL ? 'rtl' : 'ltr' }}>
                                    {isRTL ? (item.employee?.name_ar || item.employee?.name || '') : (item.employee?.name_en || item.employee?.name || '')}
                                </td>
                                <td style={{ ...colStyles.penalty_type, direction: isRTL ? 'rtl' : 'ltr' }}>
                                    {isRTL ? (item.penalty_type?.name_ar || item.penalty_type?.name || '') : (item.penalty_type?.name_en || item.penalty_type?.name || '')}
                                </td>
                                <td style={colStyles.amount}>{parseFloat(item.amount).toLocaleString()}</td>
                                <td style={colStyles.date}>{item.penalty_date}</td>
                                <td style={colStyles.status}>{t(`sidenav.hr.employeePenaltiesNamespace.${item.status}`)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EmployeePenalties;
